import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Loader2, CheckCircle2, ChevronLeft, Lightbulb, AlertOctagon,
  BookOpen, ShieldAlert, XCircle, X, Wand2, Download, Sparkles, Link2,
  AlertTriangle, GitCompare, ArrowRight, Compass, Languages, Twitter,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { handleApiError } from "@/lib/errors";
import ScanChat from "./ScanChat";
import ShareScorecardDialog from "./ShareScorecardDialog";
import VersionsModal from "./VersionsModal";

// -------- Theming ---------------------------------------------------
const LEVEL_RING = {
  low: "text-emerald-500", medium: "text-amber-500",
  high: "text-orange-500", critical: "text-rose-600",
};
const LEVEL_BANNER = {
  critical: {
    bg: "bg-rose-50", border: "border-rose-200", accent: "text-rose-700",
    chip: "bg-rose-600 text-white", bar: "bg-rose-600",
  },
  high: {
    bg: "bg-orange-50", border: "border-orange-200", accent: "text-orange-700",
    chip: "bg-orange-500 text-white", bar: "bg-orange-500",
  },
  medium: {
    bg: "bg-amber-50", border: "border-amber-200", accent: "text-amber-800",
    chip: "bg-amber-500 text-white", bar: "bg-amber-500",
  },
  low: {
    bg: "bg-emerald-50", border: "border-emerald-200", accent: "text-emerald-700",
    chip: "bg-emerald-600 text-white", bar: "bg-emerald-500",
  },
};
const SEV = {
  low:      "text-emerald-700 bg-emerald-50 border-emerald-200",
  medium:   "text-amber-700 bg-amber-50 border-amber-200",
  high:     "text-orange-700 bg-orange-50 border-orange-200",
  critical: "text-rose-700 bg-rose-50 border-rose-200",
};
const CATEGORY_LABEL = {
  commercial: "Commercial fairness",
  termination: "Termination & exit",
  liability: "Liability & indemnification",
  securities: "Securities & finder risk",
  jurisdiction: "Jurisdiction & dispute resolution",
  ip: "IP & confidentiality",
  operational: "Operational burdens",
  privacy: "Privacy & regulatory",
  employment: "Employment",
  other: "Other",
};
const CATEGORY_ORDER = [
  "securities", "liability", "termination", "commercial", "jurisdiction",
  "ip", "operational", "employment", "privacy", "other",
];
const SEVERITY_RANK = { critical: 0, high: 1, medium: 2, low: 3 };

// -------- Helpers ---------------------------------------------------
function fallbackLabel(level) {
  return ({
    critical: "EXTREME RISK", high: "HIGH RISK",
    medium: "MODERATE RISK", low: "LOW RISK",
  })[level] || "MODERATE RISK";
}

function groupIssues(issues = []) {
  const groups = new Map();
  for (const i of issues) {
    const cat = i.category && CATEGORY_LABEL[i.category] ? i.category : "other";
    if (!groups.has(cat)) groups.set(cat, []);
    groups.get(cat).push(i);
  }
  for (const arr of groups.values()) {
    arr.sort((a, b) =>
      (SEVERITY_RANK[a.severity] ?? 9) - (SEVERITY_RANK[b.severity] ?? 9)
    );
  }
  return CATEGORY_ORDER
    .filter((c) => groups.has(c))
    .map((c) => ({ category: c, label: CATEGORY_LABEL[c], items: groups.get(c) }));
}

// -------- Component -------------------------------------------------
export default function ScanDetail() {
  const { id } = useParams();
  const location = useLocation();
  // Seed state from navigate(state) when coming from a fresh runScan so the
  // queued banner renders immediately without waiting on the initial GET.
  const seed = location.state?.initial?.id === id ? location.state.initial : null;
  const [scan, setScan] = useState(seed);
  const [loading, setLoading] = useState(!seed);
  const [loadError, setLoadError] = useState(null);
  const [redlineOpen, setRedlineOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [redline, setRedline] = useState(null);
  const [redlineBusy, setRedlineBusy] = useState(false);
  const [hasRedline, setHasRedline] = useState(false);
  const [otherScansCount, setOtherScansCount] = useState(0);
  // Previous-version context — populated only when this scan is anchored to
  // a document that has at least one earlier revision in the same version
  // group. Drives the "Compare vs v{n-1}" button on the verdict card.
  const [prevVersion, setPrevVersion] = useState(null);  // {doc_id, version_number, name}
  const [currentVersionDoc, setCurrentVersionDoc] = useState(null); // {id, name, version_number} for the modal
  const [versionsModalOpen, setVersionsModalOpen] = useState(false);
  // Lazy-fetched referral code — used to attach `?ref=<code>` on the
  // Share-to-X tweet so any signup via the tweet credits this user.
  // Failure is silent (rate-limit, network) — share button still works
  // without the ref param.
  const [refCode, setRefCode] = useState("");
  useEffect(() => {
    let alive = true;
    api.get("/me/referrals").then((r) => {
      if (alive) setRefCode(r.data?.code || "");
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  // Multilingual view toggle. When the scan's native language ≠ "en", the
  // banner shows a "View in English" button — we call POST /translate?lang=en
  // (cached after first call) and overlay the translated strings on the scan.
  const [viewLang, setViewLang] = useState(null);     // null = native; "en" or other = override
  const [translating, setTranslating] = useState(false);
  const [translation, setTranslation] = useState(null);
  // Sticky mini-CTA dismissal — persisted per-scan so dismissing it on one
  // urgent scan doesn't hide it elsewhere. Survives reloads.
  const dismissKey = `agentflow:urgentCta:dismissed:${id}`;
  const [urgentCtaDismissed, setUrgentCtaDismissed] = useState(() => {
    try { return localStorage.getItem(dismissKey) === "1"; } catch { return false; }
  });
  const dismissUrgentCta = () => {
    setUrgentCtaDismissed(true);
    try { localStorage.setItem(dismissKey, "1"); } catch { /* ignore */ }
  };
  const navigate = useNavigate();
  const pollRef = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get(`/scans/${id}`);
      if (!mountedRef.current) return null;
      setScan(data);
      setLoadError(null);
      return data;
    } catch (err) {
      if (!mountedRef.current) return null;
      // Don't navigate away — show inline retry so background refreshes
      // and route races don't kick the user off the page.
      setLoadError(formatApiError(err));
      return null;
    } finally { if (!silent && mountedRef.current) setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Poll while the scan is still queued.
  useEffect(() => {
    if (!scan || scan.status !== "queued") {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const next = await load(true);
      if (next && next.status !== "queued") {
        clearInterval(pollRef.current);
        pollRef.current = null;
        if (next.status === "ready") {
          toast.success(`Scan complete — score ${next.score}`);
        } else if (next.status === "failed") {
          toast.error("Scan failed", { description: next.error || "Please try again." });
        }
      }
    }, 2500);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [scan?.status]);  // eslint-disable-line react-hooks/exhaustive-deps

  const resolve = async (issueId) => {
    try {
      await api.post(`/scans/issues/${issueId}/resolve`);
      setScan((s) => ({ ...s, issues: s.issues.map((i) => i.id === issueId ? { ...i, status: "resolved" } : i) }));
      toast.success("Issue resolved");
    } catch (err) { toast.error("Action failed", { description: formatApiError(err) }); }
  };
  const dismiss = async (issueId) => {
    try {
      await api.post(`/scans/issues/${issueId}/dismiss`);
      setScan((s) => ({ ...s, issues: s.issues.map((i) => i.id === issueId ? { ...i, status: "dismissed" } : i) }));
    } catch (err) { toast.error("Action failed", { description: formatApiError(err) }); }
  };

  const loadRedline = async () => {
    setRedlineBusy(true);
    try {
      // Try cached first
      try {
        const r = await api.get(`/scans/${id}/redline`);
        setRedline(r.data);
        return;
      } catch {
        // Falls through to generate fresh
      }
      const r = await api.post(`/scans/${id}/redline`);
      setRedline(r.data);
    } catch (err) {
      handleApiError(err, "Redline failed", navigate);
      setRedlineOpen(false);
    } finally {
      setRedlineBusy(false);
    }
  };

  useEffect(() => {
    if (redlineOpen && !redline && !redlineBusy) loadRedline();
  }, [redlineOpen]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Lazy-load context for the "Next steps" card: how many OTHER ready scans
  // exist (so we can offer Compare), and whether a redline already exists for
  // THIS scan (so we change the CTA to "View" vs "Generate").
  useEffect(() => {
    if (!scan || scan.status !== "ready") return;
    let alive = true;
    (async () => {
      try {
        const [s, r] = await Promise.allSettled([
          api.get("/scans"),
          api.get(`/scans/${id}/redline`),
        ]);
        if (!alive) return;
        if (s.status === "fulfilled") {
          const ready = (s.value.data || []).filter(
            (x) => x.status === "ready" && x.id !== id,
          );
          setOtherScansCount(ready.length);
        }
        if (r.status === "fulfilled") setHasRedline(true);
      } catch { /* non-blocking */ }
    })();
    // Separately, if this scan was run on a stored document, look for an
    // earlier revision in the same version group so we can offer a one-click
    // "Compare vs v{n-1}" CTA on the verdict card.
    if (scan.document_id) {
      (async () => {
        try {
          const { data } = await api.get(`/documents/${scan.document_id}/versions`);
          if (!alive) return;
          const versions = data?.versions || [];
          const current = versions.find((v) => v.doc_id === scan.document_id);
          if (!current) return;
          setCurrentVersionDoc({
            id: current.doc_id,
            name: current.name,
            version_number: current.version_number,
          });
          // Pick the immediate predecessor (largest version_number strictly
          // less than the current one). Falls back to null if this is v1.
          const earlier = versions
            .filter((v) => v.version_number < current.version_number)
            .sort((a, b) => b.version_number - a.version_number);
          if (earlier.length > 0) setPrevVersion(earlier[0]);
        } catch { /* document might be deleted — silently skip */ }
      })();
    }
    return () => { alive = false; };
  }, [id, scan?.status]);

  // Download the one-page Compliance Scorecard PDF. We can't just use
  // an <a download> link because the endpoint needs the auth cookie sent
  // with the request — so we fetch the blob via axios and trigger a
  // download client-side. Pro/Business gate failures surface a toast.
  const downloadScorecard = async (scanId, documentName) => {
    try {
      const { data, headers } = await api.get(
        `/scans/${scanId}/scorecard.pdf`,
        { responseType: "blob" },
      );
      const blob = new Blob([data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Prefer the filename the server sent; fall back to a safe default.
      const cd = headers?.["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/i.exec(cd);
      const safe = (documentName || "Document").replace(/[^\w\-\s]/g, "_").slice(0, 60);
      a.download = m ? m[1] : `AgentFlow Scorecard - ${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      // The body is a Blob when responseType=blob, even on errors — read it
      // back to plain text so we can detect feature_locked and surface a
      // proper upgrade CTA toast.
      try {
        const blob = err?.response?.data;
        if (blob && typeof blob.text === "function") {
          const text = await blob.text();
          const parsed = JSON.parse(text);
          // Re-shape so the central handler can detect feature_locked.
          const fakeErr = { response: { data: parsed } };
          handleApiError(fakeErr, "Scorecard download failed", navigate);
          return;
        }
      } catch { /* fall through to generic toast */ }
      toast.error("Scorecard download failed", {
        description: "Couldn't generate the scorecard. Please try again.",
      });
    }
  };

  // Toggle between the scan's native language and a translated view. First
  const toggleLanguage = async (targetLang) => {
    if (!scan) return;
    // Toggle off → back to native
    if (!targetLang || targetLang === scan.language) {
      setViewLang(null);
      setTranslation(null);
      return;
    }
    setTranslating(true);
    try {
      const { data } = await api.post(
        `/scans/${id}/translate?lang=${encodeURIComponent(targetLang)}`,
      );
      setTranslation(data);
      setViewLang(targetLang);
    } catch (err) {
      toast.error("Translation failed", { description: formatApiError(err) });
    } finally {
      setTranslating(false);
    }
  };

  if (loading && !scan) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
      </div>
    );
  }
  if (!scan) {
    return (
      <div className="space-y-6" data-testid="scan-load-error">
        <button
          onClick={() => navigate("/scans")}
          className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
        >
          <ChevronLeft className="h-4 w-4" /> All scans
        </button>
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <XCircle className="h-10 w-10 text-slate-400 mx-auto" />
          <h1 className="mt-4 font-display text-xl font-semibold text-slate-900">
            Couldn't load this scan
          </h1>
          <p className="mt-2 text-sm text-slate-600">{loadError || "Please try again."}</p>
          <Button
            onClick={() => load()}
            className="mt-5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="scan-load-retry"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // ----- Queued: AI is still working -----
  if (scan.status === "queued") {
    return (
      <div className="space-y-6" data-testid="scan-queued">
        <button
          onClick={() => navigate("/scans")}
          className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
        >
          <ChevronLeft className="h-4 w-4" /> All scans
        </button>
        <div className="rounded-2xl border border-blue-200 bg-blue-50 p-8 text-center">
          <Loader2 className="h-10 w-10 animate-spin text-blue-600 mx-auto" />
          <h1 className="mt-4 font-display text-2xl font-semibold text-slate-900">
            Analyzing {scan.document_name || "your clause"}…
          </h1>
          <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto">
            Our AI compliance agent is reviewing the document for regulatory, commercial,
            and operational risks. This typically takes 10–40 seconds.
          </p>
          <p className="mt-4 text-xs text-slate-400">
            Started {new Date(scan.created_at).toLocaleTimeString()}
          </p>
        </div>
      </div>
    );
  }

  // ----- Failed: surface the error and offer a way out -----
  if (scan.status === "failed") {
    return (
      <div className="space-y-6" data-testid="scan-failed">
        <button
          onClick={() => navigate("/scans")}
          className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
        >
          <ChevronLeft className="h-4 w-4" /> All scans
        </button>
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center">
          <XCircle className="h-10 w-10 text-rose-600 mx-auto" />
          <h1 className="mt-4 font-display text-2xl font-semibold text-slate-900">
            Scan failed
          </h1>
          <p className="mt-2 text-sm text-slate-700 max-w-md mx-auto" data-testid="scan-error-msg">
            {scan.error || "The AI service couldn't complete this scan. Please try again."}
          </p>
          <Button
            onClick={() => navigate("/documents")}
            className="mt-5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="scan-retry-cta"
          >
            Re-run from documents
          </Button>
        </div>
      </div>
    );
  }

  // Build a "view" that overlays the active translation onto the native
  // scan. When viewLang is null (or matches the scan's native lang), view
  // === scan and nothing is changed. When a translation is active, the
  // human-readable strings come from `translation`.
  const view = (() => {
    if (!viewLang || !translation || viewLang === scan.language) return scan;
    return {
      ...scan,
      language: translation.language || viewLang,
      language_label: translation.language_label,
      legal_context: translation.legal_context,
      headline: translation.headline || scan.headline,
      summary: translation.summary || scan.summary,
      executive_summary: translation.executive_summary || scan.executive_summary,
      top_concerns: translation.top_concerns || scan.top_concerns,
      fixes: translation.fixes || scan.fixes,
      regulations: translation.regulations || scan.regulations,
      risk_label: translation.risk_label || scan.risk_label,
      issues: translation.issues || scan.issues,
    };
  })();

  const level = view.level || "medium";
  const banner = LEVEL_BANNER[level] || LEVEL_BANNER.medium;
  const ringClass = LEVEL_RING[level] || "text-amber-500";
  const groups = groupIssues(view.issues || []);
  const verdict = verdictFor(level, view.score);

  // Quick-stats for the executive summary header
  const issueStats = (view.issues || []).reduce(
    (acc, i) => { acc[i.severity || "medium"] = (acc[i.severity || "medium"] || 0) + 1; return acc; },
    {}
  );
  const regulationCount = (view.regulations || []).length;

  const isUrgent = level === "critical" || level === "high";
  const urgentIssueCount = (issueStats.critical || 0) + (issueStats.high || 0);

  return (
    <div className="space-y-8" data-testid="scan-detail-page">
      <LanguageBanner
        view={view}
        nativeLang={scan.language}
        viewLang={viewLang}
        translating={translating}
        onToggleEnglish={() => toggleLanguage(viewLang === "en" ? null : "en")}
      />

      {isUrgent && !urgentCtaDismissed && (
        <UrgentMiniCta
          level={level}
          urgentIssueCount={urgentIssueCount}
          hasRedline={hasRedline}
          otherScansCount={otherScansCount}
          onPrimaryAction={
            hasRedline && otherScansCount >= 1
              ? () => navigate(`/compare?ids=${id}`)
              : () => setRedlineOpen(true)
          }
          primaryActionKind={
            hasRedline && otherScansCount >= 1 ? "compare" : (hasRedline ? "viewRedline" : "generateRedline")
          }
          onDismiss={dismissUrgentCta}
        />
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          onClick={() => navigate("/scans")}
          className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
        >
          <ChevronLeft className="h-4 w-4" /> All scans
        </button>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setShareOpen(true)}
            variant="outline"
            className="h-9 border-slate-300 text-slate-700 hover:bg-slate-50"
            data-testid="open-share-scorecard"
          >
            <Link2 className="h-3.5 w-3.5 mr-1.5" /> Share
          </Button>
          <Button
            onClick={() => downloadScorecard(id, scan.document_name)}
            variant="outline"
            className="h-9 border-slate-300 text-slate-700 hover:bg-slate-50"
            data-testid="download-scorecard"
          >
            <Download className="h-3.5 w-3.5 mr-1.5" /> Download Scorecard
          </Button>
          <Button
            onClick={() => setRedlineOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white h-9"
            data-testid="open-redline"
          >
            <Wand2 className="h-3.5 w-3.5 mr-1.5" /> Generate Suggested Redline
          </Button>
        </div>
      </div>

      {/* ============= Overall verdict ============= */}
      <div
        className={`rounded-2xl border ${banner.border} ${banner.bg} px-6 py-5 sm:px-7`}
        data-testid="overall-verdict"
      >
        <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
          <div>
            <div className={`text-xs uppercase tracking-[0.22em] font-semibold ${banner.accent}`}>
              Overall verdict
            </div>
            <p className="mt-1 font-display text-xl sm:text-2xl font-semibold tracking-tight text-slate-900 leading-snug" data-testid="verdict-line">
              {verdict}
            </p>
          </div>
          <div className="flex flex-col items-end gap-3 shrink-0">
            <RiskMeter score={scan.score} level={level} />
            <div className="flex flex-wrap items-center justify-end gap-2">
              {prevVersion && currentVersionDoc && (
                <button
                  type="button"
                  onClick={() => setVersionsModalOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white ring-1 ring-inset ring-slate-300 hover:ring-slate-900 hover:bg-slate-50 transition px-3 py-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900"
                  data-testid="compare-prev-version-btn"
                  title={`See what changed since v${prevVersion.version_number}`}
                >
                  <GitCompare className="h-3.5 w-3.5" />
                  Compare vs v{prevVersion.version_number}
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  // Privacy-safe tweet: NO document name, NO clauses, NO
                  // workspace info. Just an aggregate score + risk count +
                  // a back-link so followers can try the tool themselves.
                  const totalIssues = Object.values(issueStats || {}).reduce((a, b) => a + (b || 0), 0);
                  const lvlEmoji = level === "critical" ? "🚨"
                    : level === "high" ? "⚠️"
                    : level === "medium" ? "🔍"
                    : "✅";
                  const text = totalIssues > 0
                    ? `${lvlEmoji} Just scanned a contract with @AgentflowTool — ${totalIssues} risk${totalIssues === 1 ? "" : "s"} flagged, compliance score ${scan.score}/100. AI compliance for SMBs in under 30 seconds:`
                    : `✅ Just scanned a contract with @AgentflowTool — clean bill of health, compliance score ${scan.score}/100. AI compliance for SMBs in under 30 seconds:`;
                  const url = refCode
                    ? `https://agentflow.ventures/?ref=${refCode}`
                    : "https://agentflow.ventures";
                  const shareUrl = `https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
                  window.open(shareUrl, "_blank", "noopener,noreferrer,width=560,height=620");
                }}
                className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 hover:bg-slate-800 transition px-3 py-1.5 text-xs font-semibold text-white"
                data-testid="share-to-x-btn"
                title="Share your compliance result on X (no document details are shared)"
              >
                <Twitter className="h-3.5 w-3.5" />
                Share on X
              </button>
            </div>
          </div>
        </div>
        {/* Quick-stats strip */}
        <div className="mt-5 flex flex-wrap gap-2.5" data-testid="verdict-quickstats">
          {(issueStats.critical || 0) > 0 && (
            <QuickStat label="Critical" value={issueStats.critical}
              bg="bg-rose-100 text-rose-800 ring-rose-300" />
          )}
          {(issueStats.high || 0) > 0 && (
            <QuickStat label="High" value={issueStats.high}
              bg="bg-orange-100 text-orange-800 ring-orange-300" />
          )}
          {(issueStats.medium || 0) > 0 && (
            <QuickStat label="Medium" value={issueStats.medium}
              bg="bg-amber-100 text-amber-800 ring-amber-300" />
          )}
          {(issueStats.low || 0) > 0 && (
            <QuickStat label="Low" value={issueStats.low}
              bg="bg-emerald-100 text-emerald-800 ring-emerald-300" />
          )}
          {regulationCount > 0 && (
            <QuickStat label="Regulations flagged" value={regulationCount}
              bg="bg-blue-100 text-blue-800 ring-blue-300" />
          )}
          {Object.values(issueStats).every((v) => !v) && regulationCount === 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200 px-2.5 py-0.5 text-xs">
              <CheckCircle2 className="h-3 w-3" /> No issues flagged
            </span>
          )}
        </div>
      </div>

      {/* ============= Risk-level banner ============= */}
      <div
        className={`rounded-2xl border ${banner.border} ${banner.bg} p-6 sm:p-7`}
        data-testid="risk-level-banner"
      >
        <div className="flex flex-col sm:flex-row gap-6 items-start">
          <div className="shrink-0">
            <div className="relative h-28 w-28">
              <svg className="h-28 w-28 -rotate-90" viewBox="0 0 96 96">
                <circle cx="48" cy="48" r="42" stroke="rgba(255,255,255,0.65)" strokeWidth="10" fill="none" />
                <circle cx="48" cy="48" r="42" stroke="currentColor" strokeWidth="10" fill="none"
                  strokeDasharray={`${(view.score / 100) * 263.9} 263.9`} strokeLinecap="round" className={ringClass} />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="font-display font-semibold text-3xl text-slate-900 leading-none tabular-nums">{view.score}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">/ 100</div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <span
              className={`inline-flex items-center gap-1.5 rounded-full ${banner.chip} px-3 py-1 text-xs font-bold uppercase tracking-[0.18em]`}
              data-testid="risk-level-label"
            >
              <ShieldAlert className="h-3.5 w-3.5" />
              {view.risk_label || fallbackLabel(level)}
            </span>
            <h1 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 leading-snug" data-testid="risk-headline">
              {view.headline || view.summary}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {view.document_name || "Ad-hoc clause"}
            </p>
            {view.executive_summary && view.executive_summary !== view.headline && (
              <p className="mt-4 text-slate-700 leading-relaxed whitespace-pre-line">
                {view.executive_summary}
              </p>
            )}
          </div>
        </div>

        {view.top_concerns?.length > 0 && (
          <div className="mt-6 rounded-xl border border-slate-200 bg-white/70 p-4" data-testid="top-concerns">
            <div className={`flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold ${banner.accent}`}>
              <AlertOctagon className="h-3.5 w-3.5" /> Biggest concerns
            </div>
            <ul className="mt-2 space-y-1.5">
              {view.top_concerns.slice(0, 3).map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-800" data-testid={`top-concern-${i}`}>
                  <span className={`mt-1.5 h-1.5 w-1.5 rounded-full ${banner.bar} shrink-0`} />
                  <span className="font-medium leading-snug">{c}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* ============= Findings + Fixes ============= */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {groups.length === 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <p className="text-sm text-slate-500">No issues detected. Great work.</p>
            </div>
          )}
          {groups.map((g) => (
            <div key={g.category} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid={`group-${g.category}`}>
              <div className="flex items-center justify-between">
                <h2 className="font-display text-base sm:text-lg font-semibold text-slate-900">
                  {g.label}
                </h2>
                <span className="text-xs text-slate-500">
                  {g.items.length} finding{g.items.length === 1 ? "" : "s"}
                </span>
              </div>
              <ul className="mt-4 space-y-3">
                {g.items.map((i) => (
                  <li
                    key={i.id}
                    className={
                      "rounded-lg border p-4 " +
                      (i.status !== "open" ? "opacity-60 bg-slate-50 border-slate-200" : "border-slate-200 bg-white")
                    }
                    data-testid={`issue-${i.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900 text-sm leading-snug">{i.title}</div>
                        <p className="mt-1.5 text-sm text-slate-600 leading-relaxed whitespace-pre-line">
                          {i.detail}
                        </p>
                      </div>
                      <span className={"shrink-0 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold " + (SEV[i.severity] || SEV.medium)}>
                        {i.severity}
                      </span>
                    </div>

                    {i.references?.length > 0 && (
                      <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid={`issue-refs-${i.id}`}>
                        <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">References</span>
                        {i.references.map((r, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-600"
                          >
                            <BookOpen className="h-3 w-3" /> {r}
                          </span>
                        ))}
                      </div>
                    )}

                    {i.status === "open" && (
                      <div className="mt-3 flex gap-2">
                        <Button size="sm" onClick={() => resolve(i.id)} className="rounded-md bg-blue-600 hover:bg-blue-700 text-white" data-testid={`issue-resolve-${i.id}`}>
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Mark resolved
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => dismiss(i.id)} className="rounded-md text-slate-600">
                          Dismiss
                        </Button>
                      </div>
                    )}
                    {i.status === "resolved" && <div className="mt-2 text-xs text-emerald-700">Resolved</div>}
                    {i.status === "dismissed" && <div className="mt-2 text-xs text-slate-500">Dismissed</div>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="space-y-5">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="font-display text-lg font-semibold text-slate-900 inline-flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-amber-500" /> Recommended fixes
            </h2>
            <ul className="mt-4 space-y-3">
              {(view.fixes || []).map((f, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                  <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-white shrink-0">
                    <CheckCircle2 className="h-3 w-3" strokeWidth={3} />
                  </span>
                  <span className="leading-relaxed">{f}</span>
                </li>
              ))}
            </ul>
          </div>

          {view.regulations?.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="legal-references-card">
              <h2 className="font-display text-base font-semibold text-slate-900 inline-flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-slate-500" /> Legal references
              </h2>
              <p className="mt-1 text-xs text-slate-500">
                Frameworks and laws relevant to this document.
              </p>
              <ul className="mt-3 space-y-1.5">
                {view.regulations.map((r, i) => (
                  <li key={i} className="text-sm text-slate-700 leading-snug">
                    <span className="text-slate-400 mr-1.5">·</span>{r}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* ============= Next Steps ============= */}
      <NextStepsCard
        scanId={id}
        scanLevel={scan.level}
        otherScansCount={otherScansCount}
        hasRedline={hasRedline}
        onOpenRedline={() => setRedlineOpen(true)}
        navigate={navigate}
      />

      {/* ============= AI Chat on Report ============= */}
      <ScanChat scanId={id} scanReady={scan.status === "ready"} />

      {/* ============= Share Scorecard Dialog ============= */}
      <ShareScorecardDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        scanId={id}
        documentName={scan.document_name}
      />

      {/* ============= Redline Modal ============= */}
      <RedlineModal
        open={redlineOpen}
        busy={redlineBusy}
        data={redline}
        onClose={() => setRedlineOpen(false)}
        documentName={scan.document_name || "Document"}
        scanId={id}
      />

      {/* ============= Versions Modal ============= */}
      {versionsModalOpen && currentVersionDoc && (
        <VersionsModal
          doc={currentVersionDoc}
          onClose={() => setVersionsModalOpen(false)}
        />
      )}
    </div>
  );
}

// =========== Helpers ===========
function verdictFor(level, score) {
  const s = Number(score) || 0;
  switch (level) {
    case "critical":
      return `EXTREME RISK — Do not sign without major changes.`;
    case "high":
      return `HIGH RISK — Significant negotiation needed before signing.`;
    case "medium":
      return `MODERATE RISK — Review and clarify key clauses before signing.`;
    case "low":
      return `LOW RISK — Acceptable to sign with minor clarifications.`;
    default:
      return `Risk score: ${s}/100.`;
  }
}

/**
 * Language banner — shown at the very top of every scan.
 *
 * Always renders the "Report generated in [Language] • [Language] legal
 * context considered." line. When the scan's native language is not English,
 * also shows a "View in English" / "View original (X)" toggle.
 *
 * The toggle hits POST /scans/{id}/translate?lang=en — first call costs one
 * LLM round-trip (~1-3s); subsequent toggles are instant (cached on the scan).
 */
function LanguageBanner({ view, nativeLang, viewLang, translating, onToggleEnglish }) {
  // Active view's labels — these reflect whatever the user is currently looking
  // at (translated or native), so the "Report generated in X" line is correct.
  const langLabel = view.language_label || "English";
  const ctxLabel = view.legal_context || "U.S. + UK + EU";

  // For the toggle button copy we want the NATIVE language name (so "View
  // original (Spanish)" stays right even after the user is looking at the
  // English translation). Map the ISO code to its English name.
  const nativeNameMap = {
    en: "English", es: "Spanish", pt: "Portuguese", fr: "French", de: "German",
  };
  const nativeName = nativeNameMap[nativeLang] || "original";
  const nativeIsEnglish = (nativeLang || "en") === "en";
  const viewingTranslation = viewLang === "en" && !nativeIsEnglish;

  return (
    <div
      className="rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-2.5 flex items-center gap-3 flex-wrap"
      data-testid="language-banner"
    >
      <Languages className="h-4 w-4 text-slate-500 shrink-0" aria-hidden="true" />
      <p className="text-xs sm:text-sm text-slate-700 flex-1 min-w-0">
        Report generated in{" "}
        <span className="font-semibold text-slate-900" data-testid="lang-detected">{langLabel}</span>
        {" • "}
        <span className="text-slate-600">{ctxLabel} legal context considered.</span>
      </p>
      {!nativeIsEnglish && (
        <button
          onClick={onToggleEnglish}
          disabled={translating}
          className={
            "inline-flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-semibold transition-colors " +
            (viewingTranslation
              ? "bg-slate-200 text-slate-700 hover:bg-slate-300"
              : "bg-blue-600 text-white hover:bg-blue-700")
          }
          data-testid="lang-toggle-btn"
        >
          {translating ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin" /> Translating…
            </>
          ) : viewingTranslation ? (
            <>View original ({nativeName})</>
          ) : (
            <>View in English</>
          )}
        </button>
      )}
    </div>
  );
}

/**
 * Sticky mini-CTA bar shown only for Critical/High risk scans.
 *
 * Stays pinned just below the AppShell header (top-16) as the user scrolls
 * through long findings lists, so the next action is always one click away.
 *
 * Action priority:
 *   - No redline yet  → "Generate Suggested Redline" (most common urgent path)
 *   - Has redline + has other scans → "Compare with another contract"
 *   - Has redline, no other scans → "View Suggested Redline"
 *
 * Dismissible per-scan via localStorage (handled by parent).
 */
function UrgentMiniCta({ level, urgentIssueCount, primaryActionKind,
                         onPrimaryAction, onDismiss }) {
  const isCritical = level === "critical";
  const theme = isCritical
    ? {
        bg: "bg-rose-600",
        ring: "ring-rose-700/30",
        accent: "text-rose-50",
        ctaBg: "bg-white text-rose-700 hover:bg-rose-50",
        dismissHover: "hover:bg-rose-700/60",
      }
    : {
        bg: "bg-orange-500",
        ring: "ring-orange-600/30",
        accent: "text-orange-50",
        ctaBg: "bg-white text-orange-700 hover:bg-orange-50",
        dismissHover: "hover:bg-orange-600/60",
      };

  const headline =
    isCritical
      ? (urgentIssueCount > 0
          ? `Critical risks found${urgentIssueCount > 1 ? ` (${urgentIssueCount})` : ""}`
          : "Critical risks found")
      : (urgentIssueCount > 0
          ? `High-risk issues found${urgentIssueCount > 1 ? ` (${urgentIssueCount})` : ""}`
          : "High-risk issues found");

  const ctaLabel =
    primaryActionKind === "compare"
      ? "Compare with another contract"
      : primaryActionKind === "viewRedline"
        ? "View suggested redline"
        : "Generate Suggested Redline";

  const subtitle =
    primaryActionKind === "compare"
      ? "Recommended next step:"
      : "Recommended next step:";

  return (
    <div
      className={
        "sticky top-16 z-10 -mx-4 sm:-mx-6 lg:-mx-10 " +
        "shadow-sm ring-1 " + theme.ring + " " + theme.bg
      }
      data-testid="urgent-mini-cta"
      role="region"
      aria-label="Urgent recommended action"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-2.5 flex items-center gap-3">
        <AlertOctagon className={`h-4 w-4 shrink-0 ${theme.accent}`} aria-hidden="true" />
        <div className="min-w-0 flex-1 flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-sm font-semibold text-white" data-testid="urgent-cta-headline">
            {headline}
          </span>
          <span className={`text-sm ${theme.accent} hidden sm:inline`}>—</span>
          <span className={`text-sm ${theme.accent}`}>{subtitle}</span>
        </div>
        <button
          onClick={onPrimaryAction}
          className={
            "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs sm:text-sm font-semibold " +
            "shadow-sm transition-colors " + theme.ctaBg
          }
          data-testid="urgent-cta-action"
        >
          <span className="truncate max-w-[60vw] sm:max-w-none">{ctaLabel}</span>
          <ArrowRight className="h-3.5 w-3.5 shrink-0" />
        </button>
        <button
          onClick={onDismiss}
          className={
            "ml-1 h-7 w-7 inline-flex items-center justify-center rounded-md text-white/90 " +
            "transition-colors " + theme.dismissHover
          }
          aria-label="Dismiss recommendation"
          data-testid="urgent-cta-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}


/**
 * Recommends the natural next action for the user based on their state.
 *
 * Decision tree (ordered, top 3 shown):
 *   1. If 2+ other ready scans → Compare with another document (primary).
 *   2. If no cached redline → Generate Suggested Redline.
 *      If cached → View Suggested Redline.
 *   3. Always offer Browse Safe Templates as the soft-CTA tertiary.
 *
 * For users with only one scan, Compare is skipped and Redline is promoted
 * to the primary slot so the page still has a clear "what next?" CTA.
 */
function NextStepsCard({ scanId, scanLevel, otherScansCount, hasRedline,
                        onOpenRedline, navigate }) {
  // On critical/high scans, redline jumps to primary slot regardless of
  // how many other scans exist — the user's most urgent need is to fix
  // the dangerous clauses, not to compare against alternatives.
  const isUrgent = scanLevel === "critical" || scanLevel === "high";
  const recs = [];

  const redlineRec = {
    key: "redline",
    icon: Wand2,
    title: hasRedline ? "View suggested redline" : "Generate suggested redline",
    body: hasRedline
      ? "AgentFlow has already drafted a balanced rewrite of this contract — open it to review changes."
      : isUrgent
        ? "This document has serious risks. Let AgentFlow draft a balanced rewrite with comments explaining every change."
        : "Get an improved, balanced rewrite of this agreement with comments explaining the changes.",
    cta: hasRedline ? "Open redline" : "Generate redline",
    onClick: onOpenRedline,
    testId: "next-step-redline",
  };

  const compareRec = otherScansCount >= 1 ? {
    key: "compare",
    icon: GitCompare,
    title: "Compare with another document",
    body: otherScansCount === 1
      ? "You have 1 other completed scan — see which is safer to sign in a side-by-side breakdown."
      : `You have ${otherScansCount} other completed scans. Pick one or more to compare against this report.`,
    cta: "Compare scans",
    onClick: () => navigate(`/compare?ids=${scanId}`),
    testId: "next-step-compare",
  } : null;

  const templatesRec = {
    key: "templates",
    icon: BookOpen,
    title: "Browse safe templates",
    body: "Start your next deal from a balanced template — NDA, Consulting, Vendor MSA, Employment, Partnership and more.",
    cta: "Open templates",
    onClick: () => navigate("/templates"),
    testId: "next-step-templates",
  };

  // Order: urgent → redline first; otherwise compare first if available.
  if (isUrgent) {
    recs.push(redlineRec, ...(compareRec ? [compareRec] : []), templatesRec);
  } else if (compareRec) {
    recs.push(compareRec, redlineRec, templatesRec);
  } else {
    recs.push(redlineRec, templatesRec);
  }
  recs[0].primary = true;
  const shown = recs.slice(0, 3);

  return (
    <section
      className={
        "rounded-2xl border p-5 sm:p-6 shadow-sm " +
        (isUrgent
          ? "border-rose-200 bg-gradient-to-br from-rose-50/60 via-white to-amber-50/30"
          : "border-blue-200 bg-gradient-to-br from-blue-50/50 via-white to-white")
      }
      data-testid="next-steps-card"
    >
      <div className={
        "flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold " +
        (isUrgent ? "text-rose-600" : "text-blue-600")
      }>
        <Compass className="h-4 w-4" />
        {isUrgent ? "Recommended next steps" : "Next steps"}
      </div>
      <h2 className="mt-1.5 font-display text-xl sm:text-2xl font-semibold tracking-tight text-slate-900">
        {isUrgent
          ? "Don't sign this yet — here's what to do"
          : "What's a smart move from here?"}
      </h2>
      <p className="mt-1 text-sm text-slate-600 max-w-2xl">
        {isUrgent
          ? "AgentFlow flagged serious risks in this document. The fastest path to a safe deal:"
          : "Based on your workspace, here are the highest-leverage actions you can take in the next few minutes."}
      </p>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
        {shown.map((r) => (
          <NextStep key={r.key} {...r} accent={isUrgent ? "rose" : "blue"} />
        ))}
      </div>
    </section>
  );
}

function NextStep({ icon: Icon, title, body, cta, onClick, testId, primary, accent = "blue" }) {
  const accentMap = {
    blue: {
      primaryRing: "ring-blue-100", primaryBorder: "border-blue-300",
      primaryBorderHover: "hover:border-blue-500",
      primaryIconBg: "bg-blue-600 text-white", iconBg: "bg-blue-50 text-blue-600",
      label: "text-blue-600", cta: "text-blue-700", hoverCta: "group-hover:text-blue-700",
    },
    rose: {
      primaryRing: "ring-rose-100", primaryBorder: "border-rose-300",
      primaryBorderHover: "hover:border-rose-500",
      primaryIconBg: "bg-rose-600 text-white", iconBg: "bg-rose-50 text-rose-600",
      label: "text-rose-600", cta: "text-rose-700", hoverCta: "group-hover:text-rose-700",
    },
  };
  const a = accentMap[accent];
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={
        "group text-left rounded-xl border p-4 transition-all " +
        (primary
          ? `bg-white shadow-sm hover:shadow-md ring-2 ${a.primaryRing} ${a.primaryBorder} ${a.primaryBorderHover}`
          : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm")
      }
    >
      <div className="flex items-start gap-3">
        <div className={
          "shrink-0 h-9 w-9 rounded-lg flex items-center justify-center " +
          (primary ? a.primaryIconBg : a.iconBg)
        }>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <h3 className="font-medium text-sm text-slate-900">{title}</h3>
            {primary && (
              <span className={`text-[10px] uppercase tracking-wider font-semibold ${a.label}`}>
                Recommended
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-slate-600 leading-relaxed">{body}</p>
          <div className={
            "mt-3 inline-flex items-center gap-1 text-xs font-semibold transition-colors " +
            (primary ? a.cta : `text-slate-700 ${a.hoverCta}`)
          }>
            {cta}
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </div>
        </div>
      </div>
    </button>
  );
}

const METER_ZONES = [
  { upTo: 25, color: "bg-emerald-500", label: "Low" },
  { upTo: 50, color: "bg-amber-500", label: "Moderate" },
  { upTo: 75, color: "bg-orange-500", label: "High" },
  { upTo: 100, color: "bg-rose-600", label: "Extreme" },
];

function QuickStat({ label, value, bg }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full ring-1 ring-inset px-2.5 py-0.5 text-xs font-medium ${bg}`}>
      <span className="tabular-nums font-semibold">{value}</span> {label}
    </span>
  );
}

function RiskMeter({ score, level }) {  // eslint-disable-line no-unused-vars
  const s = Math.max(0, Math.min(100, Number(score) || 0));
  return (
    <div className="w-full sm:w-72" data-testid="risk-meter">
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
          Risk meter
        </span>
        <span className="tabular-nums text-sm font-semibold text-slate-900">{s}/100</span>
      </div>
      <div className="relative h-2.5 w-full rounded-full overflow-hidden bg-slate-200 flex">
        {METER_ZONES.map((z, i) => {
          const prev = i === 0 ? 0 : METER_ZONES[i - 1].upTo;
          return (
            <div
              key={i}
              className={`${z.color} opacity-40`}
              style={{ width: `${z.upTo - prev}%` }}
            />
          );
        })}
        <div
          className="absolute top-1/2 -translate-y-1/2 h-4 w-1 bg-slate-900 rounded-full shadow"
          style={{ left: `calc(${s}% - 2px)` }}
          aria-hidden="true"
        />
      </div>
      <div className="mt-1.5 flex justify-between text-[10px] text-slate-400 font-medium uppercase tracking-wider">
        {METER_ZONES.map((z) => (
          <span key={z.label}>{z.label}</span>
        ))}
      </div>
    </div>
  );
}

function RedlineSeverityStats({ changes }) {
  const counts = changes.reduce((a, c) => {
    a[c.severity || "medium"] = (a[c.severity || "medium"] || 0) + 1;
    return a;
  }, {});
  const items = [
    { key: "critical", label: "Critical", bg: "bg-rose-100 text-rose-800 ring-rose-300" },
    { key: "high", label: "High", bg: "bg-orange-100 text-orange-800 ring-orange-300" },
    { key: "medium", label: "Medium", bg: "bg-amber-100 text-amber-800 ring-amber-300" },
    { key: "low", label: "Low", bg: "bg-emerald-100 text-emerald-800 ring-emerald-300" },
  ];
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="redline-severity-stats">
      {items
        .filter((it) => counts[it.key])
        .map((it) => (
          <span key={it.key}
                className={`inline-flex items-center gap-1 rounded-full ring-1 ring-inset px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold ${it.bg}`}>
            <span className="tabular-nums">{counts[it.key]}</span> {it.label}
          </span>
        ))}
    </div>
  );
}


function RedlineModal({ open, busy, data, onClose, documentName, scanId }) {
  // Tracks which docx mode is currently downloading: "changelist", "inline", or false.
  const [downloadingDocx, setDownloadingDocx] = React.useState(false);

  const downloadRedlineMd = () => {
    if (!data?.rewritten_text) return;
    const blob = new Blob(
      [`# Suggested Redline — ${documentName}\n\n${data.rewritten_text}\n\n` +
       `---\n\n## Why these changes\n${data.improvements_summary}\n`],
      { type: "text/markdown" },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `agentflow-redline-${documentName.replace(/\W+/g, "-")}.md`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  // Real Word .docx with tracked changes. The endpoint requires auth, so
  // we fetch as a blob via the auth axios instance rather than using an
  // `<a download>` link (which wouldn't ship the cookie).
  const downloadRedlineDocx = async (mode = "changelist") => {
    if (!scanId) return;
    setDownloadingDocx(mode);
    try {
      const { data: blob, headers } = await api.get(
        `/scans/${scanId}/redline.docx`,
        { responseType: "blob", params: { mode } },
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = headers?.["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/i.exec(cd);
      const suffix = mode === "inline" ? " (inline)" : "";
      a.download = m
        ? m[1]
        : `AgentFlow Redline${suffix} - ${documentName.replace(/\W+/g, "_")}.docx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Word file downloaded", {
        description: mode === "inline"
          ? "Tracked changes inline. Open in Word with Review pane visible."
          : "Open in Word with the Review pane to accept / reject changes.",
      });
    } catch (err) {
      let description = "Couldn't generate the Word file.";
      try {
        const errBlob = err?.response?.data;
        if (errBlob && typeof errBlob.text === "function") {
          const text = await errBlob.text();
          const parsed = JSON.parse(text);
          if (parsed?.detail?.message) description = parsed.detail.message;
          else if (typeof parsed?.detail === "string") description = parsed.detail;
        }
      } catch { /* keep default */ }
      toast.error("Word download failed", { description });
    } finally {
      setDownloadingDocx(false);
    }
  };

  const SEV_BG = {
    low: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    medium: "bg-amber-50 text-amber-700 ring-amber-200",
    high: "bg-orange-50 text-orange-700 ring-orange-200",
    critical: "bg-rose-50 text-rose-700 ring-rose-200",
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[88vh] flex flex-col" data-testid="redline-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-blue-600" />
            Suggested Redline
            {data?.cached && (
              <span className="text-[10px] font-normal text-slate-500 ml-2">(cached)</span>
            )}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-600 mt-1">
            A balanced rewrite of <b>{documentName}</b> with dangerous clauses
            softened and a note explaining every change. Always have counsel
            review before signing.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 py-2 my-2 border-y border-slate-100">
          {busy && !data && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <p className="text-sm text-slate-600 text-center max-w-md">
                AgentFlow is rewriting the contract clause-by-clause. This
                usually takes 20–60 seconds depending on document length.
              </p>
            </div>
          )}
          {data && (
            <div className="space-y-6">
              {data.improvements_summary && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                  <div className="text-xs uppercase tracking-[0.18em] font-semibold text-emerald-700 flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5" /> Improvements summary
                  </div>
                  <p className="mt-2 text-sm text-slate-800 leading-relaxed">
                    {data.improvements_summary}
                  </p>
                </div>
              )}

              <section data-testid="redline-changes">
                <div className="flex items-end justify-between gap-3 mb-3">
                  <h3 className="font-display text-base font-semibold text-slate-900">
                    Material changes ({data.changes?.length || 0})
                  </h3>
                  <RedlineSeverityStats changes={data.changes || []} />
                </div>
                <div className="space-y-3">
                  {(data.changes || []).map((c, i) => (
                    <div key={i} className="rounded-xl border border-slate-200 bg-white">
                      <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-slate-100 bg-slate-50/60 rounded-t-xl">
                        <div className="font-medium text-sm text-slate-900">{c.section}</div>
                        <span className={`shrink-0 inline-flex items-center rounded-full ring-1 ring-inset px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold ${SEV_BG[c.severity] || SEV_BG.medium}`}>
                          {c.severity}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-100">
                        <div className="p-3">
                          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-rose-700 mb-1.5">
                            <XCircle className="h-3 w-3" /> Original
                          </div>
                          <p className="text-xs text-slate-700 leading-relaxed line-through decoration-rose-400/60 decoration-1">
                            "{c.original_excerpt}"
                          </p>
                        </div>
                        <div className="p-3 bg-emerald-50/40">
                          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-emerald-700 mb-1.5">
                            <CheckCircle2 className="h-3 w-3" /> Suggested
                          </div>
                          <p className="text-xs text-slate-800 leading-relaxed font-medium">
                            "{c.suggested_excerpt}"
                          </p>
                        </div>
                      </div>
                      <div className="px-4 py-2 border-t border-slate-100 bg-blue-50/30">
                        <p className="text-xs text-slate-700">
                          <span className="font-semibold text-blue-700">Why: </span>{c.rationale}
                        </p>
                      </div>
                      {c.negotiation_note && (
                        <div className="px-4 py-2 border-t border-slate-100 bg-slate-50">
                          <p className="text-xs text-slate-700 leading-relaxed">
                            <span className="font-semibold text-slate-900">How to position: </span>
                            {c.negotiation_note}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              {data.remaining_risks?.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <div className="text-xs uppercase tracking-[0.18em] font-semibold text-amber-700 flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5" /> Still needs human review
                  </div>
                  <ul className="mt-2 space-y-1.5">
                    {data.remaining_risks.map((r, i) => (
                      <li key={i} className="text-sm text-slate-800 flex items-start gap-2">
                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-amber-600 shrink-0" />
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <section>
                <h3 className="font-display text-base font-semibold text-slate-900 mb-3">
                  Suggested full text
                </h3>
                <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed text-slate-800 bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid="redline-text">
{data.rewritten_text}
                </pre>
              </section>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 flex-wrap">
          <Button variant="outline" className="h-9" onClick={onClose}>Close</Button>
          <Button
            variant="outline"
            className="h-9 border-slate-300"
            disabled={!data?.rewritten_text}
            onClick={downloadRedlineMd}
            data-testid="redline-download-md"
          >
            <Download className="h-3.5 w-3.5 mr-1.5" /> .md
          </Button>
          <Button
            variant="outline"
            className="h-9 border-blue-200 text-blue-700 hover:bg-blue-50 hover:text-blue-800"
            disabled={!data?.rewritten_text || downloadingDocx}
            onClick={() => downloadRedlineDocx("changelist")}
            data-testid="redline-download-docx"
            title="Section-per-change summary, ideal for a quick delta overview."
          >
            {downloadingDocx === "changelist" ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <Download className="h-3.5 w-3.5 mr-1.5" />
            )}
            Word (.docx) — Change list
          </Button>
          <Button
            className="h-9 bg-blue-600 hover:bg-blue-700 text-white"
            disabled={!data?.rewritten_text || downloadingDocx}
            onClick={() => downloadRedlineDocx("inline")}
            data-testid="redline-download-docx-inline"
            title="Reproduces the full contract with tracked changes anchored at each clause. Best for sending to opposing counsel."
          >
            {downloadingDocx === "inline" ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <Download className="h-3.5 w-3.5 mr-1.5" />
            )}
            Word (.docx) — Inline tracked changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
